import { useNavigate } from "@tanstack/react-router";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger, Button, Badge, cn } from "ui";
import {
  getInitials,
  getColorFromInitials,
  getContrastTextColor,
  formatCommentDate,
} from "utils";

import { useChatStore, useRefetchMyChats } from "./ChatContext";
import { formatLastMessage } from "./helpers";
import { useDateTranslations } from "./useDateTranslations";

interface ChatButtonProps {
  sidebar?: boolean;
}

export const ChatButton = observer(function ChatButton(props: ChatButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chatStore = useChatStore();
  const dateTranslations = useDateTranslations();
  const refetchMyChats = useRefetchMyChats();

  const [isOpen, setIsOpen] = useState(false);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);

    if (open) {
      refetchMyChats();
    }
  }

  function handleChatClick(chatId: string) {
    navigate({
      to: "/chat/$chatId",
      params: { chatId },
    });
    setIsOpen(false);
  }

  function handleShowAllClick() {
    navigate({ to: "/chat" });
    setIsOpen(false);
  }

  const button = (
    <Button
      variant={props.sidebar ? "ghost" : "outline"}
      size="icon-sm"
      className={cn(
        "relative m-0 flex gap-2",
        props.sidebar
          ? "h-8 w-full justify-start rounded-md px-2 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2!"
          : "size-10 gap-0 rounded-full p-0",
      )}
      onClick={props.sidebar ? handleShowAllClick : undefined}
    >
      <Icon name="tabler-message" />
      {props.sidebar ? (
        <span className="group-data-[collapsible=icon]:hidden">
          {t("messages")}
        </span>
      ) : null}
      {chatStore.totalUnreadCount > 0 && (
        <Badge
          aria-label={t(
            "chatList.totalUnreadAriaLabel",
            "{{count}} unread messages across all chats",
            { count: chatStore.totalUnreadCount },
          )}
          className={cn(
            "absolute flex size-5 items-center justify-center bg-red-500 px-1 text-xs text-white",
            props.sidebar
              ? "right-1 group-data-[collapsible=icon]:-top-1 group-data-[collapsible=icon]:-right-1"
              : "-top-1 -right-1",
          )}
        >
          <span aria-hidden="true">{chatStore.totalUnreadCount}</span>
        </Badge>
      )}
    </Button>
  );

  if (props.sidebar) {
    return button;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        side={props.sidebar ? "right" : "bottom"}
        align="end"
      >
        <div className="border-0 shadow-none">
          <div className="pb-2 pt-4 px-4 flex flex-col items-center">
            <div className="text-lg font-medium">{t("messages")}</div>
          </div>
          <div className="p-0">
            {chatStore.chats.length === 0 ? (
              <div className="py-6 px-4 text-center text-muted-foreground">
                {t("noMessages")}
              </div>
            ) : (
              <div className="max-h-80 overflow-auto">
                {chatStore.chats.map((chat) => {
                  const lastMessagePreview = formatLastMessage(
                    chat.lastMessage,
                    t,
                  );
                  const initials = getInitials(chat.name);
                  const bgColor = getColorFromInitials(initials);
                  const textColor = getContrastTextColor(bgColor);
                  const formattedDate = chat.lastMessage
                    ? formatCommentDate({
                        dateString: chat.lastMessage.createdAt,
                        translations: dateTranslations,
                      })
                    : null;

                  return (
                    <div
                      key={chat.id}
                      className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        handleChatClick(chat.id);
                      }}
                    >
                      <div
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full font-semibold"
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                        }}
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-gray-900 truncate ${chat.unreadCount > 0 ? "font-semibold" : ""}`}
                        >
                          {chat.name}
                        </div>
                        <div
                          className={`text-sm truncate ${
                            chat.unreadCount > 0
                              ? "font-semibold text-blue-500"
                              : "text-gray-500"
                          }`}
                        >
                          {lastMessagePreview}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2">
                        {formattedDate && (
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formattedDate}
                          </span>
                        )}
                        {chat.unreadCount > 0 && (
                          <div
                            aria-label={t(
                              "chatList.unreadAriaLabel",
                              "{{count}} unread",
                              { count: chat.unreadCount },
                            )}
                            className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-semibold text-white"
                          >
                            <span aria-hidden="true">{chat.unreadCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="p-2 bg-slate-50 border-t rounded-b-md">
            <Button
              variant="ghost"
              className="w-full text-sm h-auto py-1.5"
              onClick={handleShowAllClick}
            >
              {t("showAllChats")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});
